import Circular from "@/components/Circular";
import NotFoundPostView from "@/components/NotFoundPostView";
import PostItem from "@/features/post/components/PostItem";
import PostItemList from "@/features/post/components/PostItemList";
import UserDetailGroup from "@/features/user/components/UserDetailGroup";
import DefaultLayout from "@/layouts/DefaultLayout";
import { notFoundWrapper } from "@/utils/notFoundWrapper";
import publicPath from "@/utils/publicPath";
import { PostResponse, UserDetailResponse } from "@/web-client/api";
import { UserTabType } from "@/consts/tabType";
import StateContext from "@/contexts/StateContext";
import usePageView from "@/hooks/usePageView";
import usePagination from "@/hooks/usePagination";
import useUserDetail from "@/features/user/hooks/useUserDetail";
import { GetStaticPaths, GetStaticProps, NextPage } from "next";
import { NextSeo } from "next-seo";
import InfiniteScroll from "react-infinite-scroll-component";
import client from "@/utils/api/client";

interface Props {
  initialUser: UserDetailResponse;
}

const UserPostsPage: NextPage<Props> = ({ initialUser }) => {
  usePageView();
  const { data: user, setUser } = useUserDetail(initialUser.id, initialUser);
  const { data, dataLength, next, hasNext, notFound, updateItem } =
    usePagination<PostResponse>(
      `/users/${user.id}/posts`,
      20,
      async ({ limit, cursor }) => {
        const { data } = await client.usersIdPostsGet({
          id: user.id,
          limit,
          cursor: cursor as number,
        });

        return data;
      },
      { waitForAuth: true },
    );

  return (
    <DefaultLayout>
      <NextSeo
        title={`${user.screen_name}さんの写真一覧`}
        openGraph={
          user.icon ? { images: [{ url: user.icon.urls.original }] } : {}
        }
        twitter={{
          cardType: "summary",
        }}
        canonical={publicPath(`/users/${user.id}/posts`)}
      />
      <StateContext.Provider value={{ update: setUser }}>
        <main className="Container user">
          <UserDetailGroup user={user} tabType={UserTabType.Post}>
            <InfiniteScroll
              style={{ overflow: "visible" }}
              dataLength={dataLength}
              next={next}
              hasMore={hasNext}
              loader={<Circular />}
            >
              <PostItemList
                posts={data}
                onRender={(post) => (
                  <PostItem
                    key={post.id}
                    post={post}
                    mutate={(updatedPost) => updateItem(updatedPost, false)}
                  />
                )}
              />
            </InfiniteScroll>

            {notFound && <NotFoundPostView user={user} />}
          </UserDetailGroup>
        </main>
      </StateContext.Provider>
    </DefaultLayout>
  );
};

export default UserPostsPage;

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  return notFoundWrapper<Props>(async () => {
    const { data, status } = await client.usersIdGet(
      { id: Number(params.id) },
      {
        validateStatus(status: number) {
          return status >= 200 && status < 400;
        },
      },
    );

    if (status === 301) {
      return {
        redirect: {
          destination: `/makers/${data.id}`,
          permanent: true,
        },
      };
    }

    return {
      props: {
        initialUser: data,
      },
      revalidate: 1,
    };
  });
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: "blocking",
});
